import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GIButton } from 'app-components';
import { z } from 'zod';
import { FormattedMessage, useIntl } from 'react-intl';
import parse from 'html-react-parser';
import { useDispatch } from 'react-redux';

import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import NavBarContainer from '../../../containers/common/NavBarContainer';
import Dropdown from '../../../lib/inputs/Dropdown';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { api } from '../../common-utils-services/axiosUtil';
import { configs } from '../../../constants/configs/configs';
import { IEX_STATE_CODES } from '../../../constants/configs/states';
import { useAppSelector } from '../../../hooks';
import { environmentSelector } from '../../../selectors/commonSelectors';

import { ILoadPlansFields, IUploadPlansResponse } from '../types/uploadPlan';
import {
    ALLOWED_FILE_TYPES_FOR_PLANS,
    MN_ALLOWED_FILE_TYPES_FOR_PLANS,
} from '../../../constants/common/fileTypes';
import { STATUS_CODE } from '../../../constants/common/apiResponse';
import { INTEGER_REGEX } from '../../../utils/validators/regex';
import { useIssuerList } from '../hooks/useIssuerList';
import SideBar from '../components/SideBar';
import useLoadFileValidation from '../hooks/useLoadFileValidation';
import { showAdminPlanDrugModal } from '../utils';
import {
    FlexRow,
    StyledDiv,
    StyledDropdown,
    StyledErrorMessage,
    StyledFileInput,
    StyledForm,
    StyledHeading,
    StyledLabel,
    StyledLayoutContainer,
} from '../styles';

const LoadPlans: React.FC = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { stateCode } = useAppSelector(environmentSelector);
    const { issuerDropdownOptions } = useIssuerList(intl);

    const formSchema = z.object({
        issuer: z
            .string()
            .min(1, { message: intl.formatMessage({ id: 'admin.errors.issuerRequired' }) })
            .regex(INTEGER_REGEX, { message: intl.formatMessage({ id: 'admin.errors.carrierValidation' }) }),

        files: z.any().refine(
            async value => {
                const isFileValid = validateFile(value);

                if (!isFileValid) {
                    return false;
                }
                return true;
            },
            { message: intl.formatMessage({ id: 'admin.loadPlans.errors.file.requiredFILE' }) },
        ),
    });

    const {
        register,
        setValue,
        watch,
        reset,
        formState: { isDirty, isValid, errors },
    } = useForm<ILoadPlansFields>({
        defaultValues: { issuer: '', files: [] },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const values = watch();

    const { validateFile, resetIsFileValid, fileValidMessage, isInvalidFileList } = useLoadFileValidation({
        stateCode,
        carrier: values.issuer,
        intl,
    });

    const handleFieldChange = (name: keyof ILoadPlansFields, value: string) => {
        setValue(name, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleSubmit = async e => {
        e.preventDefault();
        const url = configs.endpoints.UPLOAD_PLANS;
        const formData = new FormData();
        formData.append('carrier', values.issuer);
        formData.append('carrierText', issuerDropdownOptions.find(issuer => issuer.value == values.issuer)?.text || '');
        for (const file of values.files) {
            formData.append('fileUploadPlans', file);
        }

        try {
            const response = await api.post<IUploadPlansResponse>(url, formData);
            if (response?.data) {
                if (response.data.status === STATUS_CODE.SUCCESS) {
                    showAdminPlanDrugModal(
                        dispatch,
                        STATUS_CODE.SUCCESS,
                        'admin.loadPlans.uploadPlan.success',
                        'admin.loadPlans.heading',
                    );
                } else if (response.data.status === STATUS_CODE.FAIL) {
                    showAdminPlanDrugModal(
                        dispatch,
                        STATUS_CODE.FAIL,
                        response.data.errorMessage,
                        'admin.loadPlans.heading',
                    );
                }
            }
            reset();
        } catch (error) {
            throw new Error(error);
        }
    };

    const renderFileNames = () => {
        return values.files.length > 0 ? (
            <div>
                <br />
                {Array.from(values.files).map((file: File, index: number) => (
                    <>
                        <div key={`file_${index}`}>{file.name}</div>
                    </>
                ))}
            </div>
        ) : (
            ''
        );
    };

    const handleCancel = () => {
        reset();
        resetIsFileValid();
    };

    const sidebarSteps = {
        heading: 'admin.loadPlans.sideBar.heading',
        steps: ['admin.loadPlans.sideBar.content1', 'admin.loadPlans.sideBar.content2', 'admin.loadPlans.sideBar.content3'],
    };

    return (
        <StyledLayoutContainer id="loadPlans" $stateCode={stateCode}>
            <div className="grid-container">
                <div className="grid-row">
                    <NavBarContainer navBarClass="ticket-mgmt-top-nav grid-col-12" />
                </div>
                <br />
                <div>
                    <h1>
                        <FormattedMessage id={'admin.loadPlans.heading'}></FormattedMessage>
                    </h1>
                </div>

                <FlexRow>
                    <SideBar {...sidebarSteps} stateCode={stateCode} />

                    <StyledDiv $stateCode={stateCode}>
                        <StyledHeading $stateCode={stateCode} />
                        <StyledForm onSubmit={handleSubmit}>
                            <StyledDropdown $stateCode={stateCode}>
                                <StyledLabel htmlFor="issuer">
                                    {parse(intl.formatMessage({ id: 'admin.loadPlans.form.issuer' }))}
                                    <span className="text-red"> *</span>
                                </StyledLabel>
                                <Dropdown
                                    label={{ labelText: '' }}
                                    inputData={{
                                        id: 'issuer',
                                        options: issuerDropdownOptions,
                                        type: INPUT_TYPES.DROPDOWN,
                                        value: values.issuer,
                                        ...register('issuer'),
                                        inputClass: ['small-width'],
                                    }}
                                    inputActions={{
                                        handleFieldChange: val => {
                                            handleFieldChange('issuer', val);
                                        },
                                    }}
                                />
                                <ConditionalRendering isTrue={!!errors.issuer}>
                                    <StyledErrorMessage>{errors.issuer?.message} </StyledErrorMessage>
                                </ConditionalRendering>
                            </StyledDropdown>
                            <div>
                                <StyledLabel htmlFor="files">
                                    {parse(intl.formatMessage({ id: 'admin.loadPlans.form.files' }))}
                                    <span className="text-red"> *</span>
                                </StyledLabel>
                                <br />
                                <StyledFileInput
                                    id="files"
                                    type="file"
                                    disabled={!values.issuer}
                                    multiple
                                    {...register('files')}
                                    accept={
                                        stateCode === IEX_STATE_CODES.MN
                                            ? MN_ALLOWED_FILE_TYPES_FOR_PLANS
                                            : ALLOWED_FILE_TYPES_FOR_PLANS
                                    }
                                />
                                <br />
                                {renderFileNames()}
                                <ConditionalRendering isTrue={!!errors.files}>
                                    <StyledErrorMessage>{parse(fileValidMessage ?? errors.files)}</StyledErrorMessage>
                                </ConditionalRendering>
                            </div>

                            <FlexRow $isPadding>
                                <GIButton
                                    id="cancel"
                                    onClickHandler={handleCancel}
                                    variant="secondary"
                                    labelId="button.cancel"
                                    type="button"
                                    dataTestId="cancel-button"
                                />
                                <GIButton
                                    id="primary"
                                    type="submit"
                                    variant="primary"
                                    labelId="button.save"
                                    isDisabled={!(isDirty && isValid) || isInvalidFileList}
                                    dataTestId="save-button"
                                />
                            </FlexRow>
                        </StyledForm>
                    </StyledDiv>
                </FlexRow>
            </div>
        </StyledLayoutContainer>
    );
};

export default LoadPlans;
